import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip.tsx";
import {
  openTaskBadgeLabel,
  openTaskToggleLabel,
  type OmniboxTasks,
} from "@/components/tasks/OmniboxTasks.tsx";
import { cn } from "@/lib/utils.ts";
import type { GetThreadResp } from "@/types/thread.ts";
import type { ThreadConfig } from "@/types/threadConfig.ts";
import { CalendarPlus, Cpu, ListChecks, Paperclip, Plus } from "lucide-react";
import { useRef, type ComponentProps, type ReactNode } from "react";
import { ModelSwitcher } from "./ModelSwitcher.tsx";
import { QuickSchedule } from "./QuickSchedule.tsx";

/**
 * One entry in the menu. Wears the same metrics a `DropdownMenuItem` does, so
 * this list reads as the app's other menus even though it is a popover: two of
 * its entries open surfaces of their own, and a menu item cannot open one
 * without the menu closing over it.
 */
const ToolsMenuRow = ({
  icon: Icon,
  iconClassName,
  label,
  trailing,
  ...props
}: {
  icon: typeof Paperclip;
  iconClassName?: string;
  label: string;
  /** Sits at the end of the row — a count, a current value. */
  trailing?: ReactNode;
} & ComponentProps<typeof Button>) => (
  <Button
    type="button"
    variant="blank"
    size="fit"
    {...props}
    className={cn(
      "w-full justify-start gap-2 rounded-sm px-2 py-2.5 text-sm font-normal",
      "hover:bg-background focus-visible:bg-background",
      props.className,
    )}
  >
    <Icon className={cn("size-4 shrink-0", iconClassName)} />
    <span className="flex-1 text-left">{label}</span>
    {trailing}
  </Button>
);

/**
 * The composer's secondary controls, collapsed behind one `+`.
 *
 * None of them is needed to type a message — attaching a file, opening the task
 * panel, changing the model, drafting a quick invite are all things you go
 * looking for — so they fold away and the composer stays one line at any length.
 *
 * A labelled list, like the app's other menus, rather than a tray of bare glyphs
 * to recognise. It stays a popover rather than a dropdown menu because two of
 * the entries open surfaces of their own, and a menu item closes the menu before
 * its surface can appear — so those two hand this a row to use as their trigger
 * instead.
 */
export const OmniboxToolsMenu = ({
  onAttach,
  tasks,
  thread,
  onConfigChange,
  onQuickSchedule,
}: {
  onAttach: () => void;
  tasks: OmniboxTasks;
  thread?: GetThreadResp;
  onConfigChange?: (config: ThreadConfig) => void;
  /** Only the new-thread omnibox offers it, so absent means "not here". */
  onQuickSchedule?: (messageText: string) => Promise<void>;
}) => {
  // Whether this open came from a pointer. Radix moves focus into the panel on
  // open and back to the trigger on close, and for a pointer user that takes
  // the cursor out of the field — which on mobile drops the keyboard and folds
  // the omnibox back to its resting width, mid-sentence, for someone who only
  // reached for an attachment. For a keyboard user the same move is the only
  // way in: the rows sit in a portal, so Tab follows the trigger's place in the
  // document rather than entering the panel. So the suppression is scoped to
  // the gesture that does not need it.
  const openedByPointer = useRef(false);

  return (
    <Popover>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label="More"
              className="size-8 shrink-0 rounded-full hover:bg-secondary"
              data-statsig-name="omnibox-tools-menu"
              onPointerDown={() => {
                openedByPointer.current = true;
              }}
              onKeyDown={() => {
                openedByPointer.current = false;
              }}
              // Reaching for this with a pointer must not take the cursor out of
              // the field. A button takes focus when it is pressed, and that alone
              // was enough to end the sentence being written. Cancelling the press
              // suppresses only that focus move: Radix opens the panel on click,
              // which still fires, and keyboard activation never goes through
              // here at all.
              onMouseDown={(event) => event.preventDefault()}
            >
              <Plus className="size-4" />
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="top">Attachments and settings</TooltipContent>
      </Tooltip>
      {/* `side="top"`: the omnibox sits at the bottom of every surface that
          renders it, so downward is off-screen. */}
      <PopoverContent
        side="top"
        align="start"
        className="w-56 p-1"
        onOpenAutoFocus={(event) => {
          if (openedByPointer.current) event.preventDefault();
        }}
        onCloseAutoFocus={(event) => {
          if (openedByPointer.current) event.preventDefault();
        }}
      >
        <ToolsMenuRow
          icon={Paperclip}
          label="Attach a file"
          onClick={onAttach}
          data-statsig-name="omnibox-file-attach"
        />
        {tasks.enabled && (
          <ToolsMenuRow
            icon={ListChecks}
            // Blue is this app's mark for an internal-only affordance, the same
            // treatment the account-wide tasks button and the debug menu carry.
            iconClassName="text-blue-600"
            label="Tasks"
            // The row reads "Tasks"; the name carries the count, which the capped
            // badge beside it cannot.
            aria-label={openTaskToggleLabel(tasks.openCount)}
            onClick={tasks.toggleExpanded}
            aria-pressed={tasks.isExpanded}
            trailing={
              tasks.openCount > 0 ? (
                <span className="rounded-full bg-blue-600 px-1.5 text-[10px] font-semibold leading-4 tabular-nums text-white">
                  {openTaskBadgeLabel(tasks.openCount)}
                </span>
              ) : undefined
            }
            data-statsig-name="omnibox-tasks-toggle"
          />
        )}
        {onQuickSchedule && (
          <QuickSchedule
            onSubmit={onQuickSchedule}
            renderTrigger={(open) => (
              <ToolsMenuRow
                icon={CalendarPlus}
                label="Schedule a meeting"
                onClick={open}
                data-statsig-name="omnibox-quick-schedule-open"
              />
            )}
          />
        )}
        <ModelSwitcher
          thread={thread}
          onConfigChange={onConfigChange}
          trigger={
            <ToolsMenuRow
              icon={Cpu}
              iconClassName="text-blue-600"
              label="Model and agent"
              data-statsig-name="omnibox-model-switcher"
            />
          }
        />
      </PopoverContent>
    </Popover>
  );
};
